// Access Level Configuration (Access Center / Access Configuration)
//
// FULLY DYNAMIC. Rows come from `access_levels` table - no hardcoded
// catalog. The UI mirrors whatever the DB has; the only client-side
// enforcement is "Admin column is always ON".
//
// Backend:
//   GET  /api/AccessCenter/get-access      - latest row per (category, page_name)
//   POST /api/AccessCenter/save            - replaces the whole matrix (DELETE + INSERT in txn)
//   GET  /api/employee/logined_employee    - Admin gate

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { dnAlert } from "./utils/dNews_Alert";
import logo_eagle from "./assets/img/logo_eagle.png";
import { apiFetch } from "./utils/api";
import "./Access_level_configuration.css";

// Shared react-select styling so the toolbar dropdown matches the look
// used in Employee_Center / Payslip_Management (rounded card, slate border,
// orange focus ring). menuPortal at z=9999 ensures the menu draws over
// the sticky table header below the toolbar.
const ALC_SELECT_STYLES = {
    container: p => ({ ...p, width: 240 }),
    control: (p, s) => ({
        ...p,
        minHeight: 40,
        borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
        padding: "10px 12px",
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
};

const ROLES = ["Admin", "Sub Admin", "Manager", "User"];

// Category accent dots - purely visual, derived from the row's category
// name. Falls back to slate for any new category the admin invents.
const CATEGORY_DOTS = {
    "dAssist": "#2563EB",
    "dBug": "#7C3AED",
    "dForms": "#0891B2",
    "dTime": "#16A34A",
    "dEpr": "#E8520A",
    "dSpr": "#DB2777",
    "dSlip": "#CA8A04",
    "Manage Employee": "#64748B",
    "Access Center": "#0D1B2A",
    "Inside D": "#9333EA",
};
function catDot(category) {
    return CATEGORY_DOTS[category] || "#94A3B8";
}

const Access_level_configuration = ({ navSize }) => {
    const navigate = useNavigate();

    const [accessData, setAccessData] = useState([]);
    const [backupData, setBackupData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [categoryFilter, setCategoryFilter] = useState("");

    // Page-level access enforcement is handled by <ProtectedRoute> in
    // App.js (consults the access_levels matrix). No need for a hardcoded
    // role check here - the matrix is now the single source of truth.

    // Load matrix from DB - dedupe defensively in case the cleanup SQL
    // hasn't been run yet (the latest row per category+page wins).
    const fetchMatrix = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/AccessCenter/get-access");
            const data = await res.json();
            if (!data.success) throw new Error(data.error || "Load failed");
            const seen = new Set();
            const rows = [];
            (data.data || []).forEach(r => {
                const key = `${r.category}::${r.page_name}`;
                if (seen.has(key)) return;
                seen.add(key);
                rows.push({
                    category: r.category,
                    page: r.page_name,
                    access_id: r.access_id,
                    Admin: true,                                  // always ON
                    "Sub Admin": r.subadmin_access === 1,
                    Manager: r.manager_access === 1,
                    User: r.user_access === 1,
                });
            });
            setAccessData(rows);
        } catch (err) {
            console.error("Failed to load access matrix:", err);
            dnAlert.error("Load failed", err.message || "Couldn't load the access matrix.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMatrix(); }, []);

    const toggleAccess = (rowIndex, role) => {
        if (!isEditing) return;
        if (role === "Admin") return;   // Admin column is always locked ON
        setAccessData(prev => {
            const next = [...prev];
            next[rowIndex] = { ...next[rowIndex], [role]: !next[rowIndex][role] };
            return next;
        });
    };

    const handleEdit = () => {
        setBackupData(JSON.parse(JSON.stringify(accessData)));
        setIsEditing(true);
    };

    const handleCancel = () => {
        setAccessData(JSON.parse(JSON.stringify(backupData)));
        setIsEditing(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await apiFetch("/api/AccessCenter/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(accessData),
            });
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Saved", `${data.saved || accessData.length} access rows updated.`);
                setIsEditing(false);
                await fetchMatrix();   // re-fetch so we see exactly what the DB has
            } else {
                dnAlert.error("Save failed", data.error || "Try again.");
            }
        } catch (err) {
            dnAlert.error("Error", err.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    // Distinct categories in display order (first-seen wins)
    const categoryOrder = useMemo(() => {
        const seen = new Set();
        accessData.forEach(r => seen.add(r.category));
        return Array.from(seen);
    }, [accessData]);

    const filteredData = useMemo(() => {
        if (!categoryFilter) return accessData;
        return accessData.filter(r => r.category === categoryFilter);
    }, [accessData, categoryFilter]);

    const stats = useMemo(() => {
        const total = accessData.length;
        const categories = new Set(accessData.map(r => r.category)).size;
        const subAdmin = accessData.filter(r => r["Sub Admin"]).length;
        const manager = accessData.filter(r => r.Manager).length;
        const user = accessData.filter(r => r.User).length;
        return { total, categories, subAdmin, manager, user };
    }, [accessData]);

    return (
        <div className="alc-container">
            <div className={`alc-dashboard ${navSize}`}>

                {/* ── Header (matches Payslip Management / Generate Payslips) ── */}
                <div className="alc-header-wrapper">
                    <h2 className="alc-header-h2">
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                        </a>
                        Access Level Configuration
                    </h2>
                    <p className="alc-subtitle">
                        Define which pages each role tier can access. Admin column is locked ON for every page.
                    </p>
                </div>

                {/* ── Stat tiles ──────────────────────────────────────────── */}
                <div className="alc-stats-grid">
                    <div className="alc-stat alc-stat-blue">
                        <div className="alc-stat-label">TOTAL PAGES</div>
                        <div className="alc-stat-num">{stats.total}</div>
                    </div>
                    <div className="alc-stat alc-stat-indigo">
                        <div className="alc-stat-label">CATEGORIES</div>
                        <div className="alc-stat-num">{stats.categories}</div>
                    </div>
                    <div className="alc-stat alc-stat-amber">
                        <div className="alc-stat-label">SUB ADMIN ACCESS</div>
                        <div className="alc-stat-num">{stats.subAdmin}</div>
                    </div>
                    <div className="alc-stat alc-stat-green">
                        <div className="alc-stat-label">MANAGER ACCESS</div>
                        <div className="alc-stat-num">{stats.manager}</div>
                    </div>
                    <div className="alc-stat alc-stat-slate">
                        <div className="alc-stat-label">USER ACCESS</div>
                        <div className="alc-stat-num">{stats.user}</div>
                    </div>
                </div>

                {/* ── Toolbar ─────────────────────────────────────────────── */}
                <div className="alc-toolbar">
                    <div className="alc-toolbar-left">
                        <div className="alc-field">
                            <label>FILTER BY CATEGORY</label>
                            {(() => {
                                const opts = [
                                    { value: "", label: `All categories (${categoryOrder.length})` },
                                    ...categoryOrder.map(c => ({ value: c, label: c })),
                                ];
                                return (
                                    <Select
                                        options={opts}
                                        value={opts.find(o => o.value === categoryFilter)}
                                        onChange={opt => setCategoryFilter(opt ? opt.value : "")}
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={ALC_SELECT_STYLES}
                                    />
                                );
                            })()}
                        </div>
                        {isEditing && (
                            <div className="alc-edit-banner">
                                <span aria-hidden="true">✎</span> Edit mode is on - changes are unsaved until you click Save Changes.
                            </div>
                        )}
                    </div>
                    <div className="alc-toolbar-right">
                        {!isEditing && (
                            <button type="button" className="alc-btn alc-btn-primary" onClick={handleEdit}>
                                ✎ Edit Access
                            </button>
                        )}
                        {isEditing && (
                            <>
                                <button type="button" className="alc-btn alc-btn-ghost" onClick={handleCancel} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="button" className="alc-btn alc-btn-save" onClick={handleSave} disabled={saving}>
                                    {saving ? "Saving..." : "✓ Save Changes"}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Table ───────────────────────────────────────────────── */}
                <div className="alc-table-wrap">
                    {loading ? (
                        <div className="alc-loading">Loading access matrix...</div>
                    ) : filteredData.length === 0 ? (
                        <div className="alc-loading">No rows found.</div>
                    ) : (
                        <table className="alc-table">
                            <thead>
                                <tr>
                                    <th className="alc-col-cat">Category</th>
                                    <th className="alc-col-page">Page</th>
                                    {ROLES.map(role => (
                                        <th key={role} className="alc-col-role">{role}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((row, rowIndex) => {
                                    const realIndex = accessData.findIndex(
                                        r => r.category === row.category && r.page === row.page
                                    );
                                    const showCategory =
                                        rowIndex === 0 ||
                                        filteredData[rowIndex - 1].category !== row.category;
                                    return (
                                        <tr key={`${row.category}-${row.page}`}>
                                            <td className="alc-col-cat">
                                                {showCategory && (
                                                    <span className="alc-cat-pill">
                                                        <span className="alc-cat-dot" style={{ background: catDot(row.category) }} />
                                                        {row.category}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="alc-col-page">{row.page}</td>
                                            {ROLES.map(role => {
                                                const forced = role === "Admin";
                                                return (
                                                    <td key={role} className="alc-col-role">
                                                        <label className={`alc-switch ${forced ? "alc-switch-locked" : ""}`}
                                                            title={forced ? "Admin always has access" : ""}>
                                                            <input type="checkbox"
                                                                disabled={forced || !isEditing}
                                                                checked={forced ? true : !!row[role]}
                                                                onChange={() => toggleAccess(realIndex, role)} />
                                                            <span className="alc-slider" />
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Access_level_configuration;
